// 🧾 Misión › Reporte de facturación — facturas AUTORIZADAS emitidas a los centros de
// costo del usuario logoneado (RPC reporte_facturacion_mision, filtrado server-side).
// Columnas ordenables asc/desc y exportación a XLS.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReceiptText, Loader2, FileText, Download, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const inputCls = 'mt-1 h-9 rounded-md border border-input bg-background px-2 text-sm';
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);
const money = (v: number, moneda?: string) => {
  try { return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: (moneda || 'USD').toUpperCase() }).format(Number(v) || 0); }
  catch { return `${(Number(v) || 0).toFixed(2)} ${moneda || ''}`.trim(); }
};

interface Fila {
  id: string; fecha_emision: string; serie_comprobante: string | null; tipo_comprobante: string;
  paciente: string | null; centro_costo: string | null; observaciones: string | null;
  importe_total: number; moneda: string; xml_url: string | null; ride_url: string | null;
}

type Col = 'fecha' | 'comprobante' | 'tipo' | 'paciente' | 'centro' | 'observacion' | 'monto';

export function MisionReporteFacturacion() {
  const { data: filas = [], isFetching } = useQuery<Fila[]>({
    queryKey: ['reporte-fact-mision'],
    queryFn: async () => (await apiClient.post('/rpc/reporte_facturacion_mision', {}, FIN)).data || [],
  });

  // Filtros
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [comprobante, setComprobante] = useState('');
  const [paciente, setPaciente] = useState('');
  const [centro, setCentro] = useState('');
  const centrosOpts = useMemo(() => ([...new Set(filas.map((f) => f.centro_costo).filter(Boolean))] as string[]).sort(), [filas]);
  const filtradas = useMemo(() => filas.filter((f) =>
    (!desde || f.fecha_emision >= desde) &&
    (!hasta || f.fecha_emision <= hasta) &&
    (!comprobante || (f.serie_comprobante || '').toLowerCase().includes(comprobante.toLowerCase())) &&
    (!paciente || (f.paciente || '').toLowerCase().includes(paciente.toLowerCase())) &&
    (!centro || f.centro_costo === centro)
  ), [filas, desde, hasta, comprobante, paciente, centro]);

  const [orden, setOrden] = useState<{ col: Col; dir: 'asc' | 'desc' }>({ col: 'fecha', dir: 'desc' });
  const ordenar = (col: Col) => setOrden((o) => (o.col === col ? { col, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  const flecha = (col: Col) => (orden.col === col ? (orden.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const ordenadas = useMemo(() => {
    const arr = [...filtradas];
    const s = orden.dir === 'asc' ? 1 : -1;
    const txt = (v: string | null) => (v || '');
    arr.sort((a, b) => {
      switch (orden.col) {
        case 'monto': return (Number(a.importe_total) - Number(b.importe_total)) * s;
        case 'comprobante': return txt(a.serie_comprobante).localeCompare(txt(b.serie_comprobante), 'es') * s;
        case 'tipo': return a.tipo_comprobante.localeCompare(b.tipo_comprobante, 'es') * s;
        case 'paciente': return txt(a.paciente).localeCompare(txt(b.paciente), 'es') * s;
        case 'centro': return txt(a.centro_costo).localeCompare(txt(b.centro_costo), 'es') * s;
        case 'observacion': return txt(a.observaciones).localeCompare(txt(b.observaciones), 'es') * s;
        default: return String(a.fecha_emision).localeCompare(String(b.fecha_emision)) * s;
      }
    });
    return arr;
  }, [filtradas, orden]);

  // Total al pie, agrupado por moneda (por si hay multimoneda).
  const totales = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of ordenadas) { const k = (f.moneda || 'USD').toUpperCase(); m.set(k, (m.get(k) || 0) + (Number(f.importe_total) || 0)); }
    return [...m.entries()];
  }, [ordenadas]);

  const exportarXls = async () => {
    const datos = ordenadas.map((f) => ({
      Fecha: f.fecha_emision,
      'N° Comprobante': f.serie_comprobante || '',
      Tipo: f.tipo_comprobante,
      Paciente: f.paciente || '',
      Observación: f.observaciones || '',
      Monto: Number(f.importe_total) || 0,
      Moneda: f.moneda || '',
      'Centro de costo': f.centro_costo || '',
    }));
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.json_to_sheet(datos);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Facturación');
    XLSX.writeFile(wb, `reporte_facturacion_${fechaLocal()}.xlsx`);
  };

  const Th = ({ col, children, right }: { col: Col; children: React.ReactNode; right?: boolean }) => (
    <th className={`py-2 pr-3 ${right ? 'text-right' : ''}`}>
      <button type="button" className="hover:text-foreground" onClick={() => ordenar(col)}>{children}{flecha(col)}</button>
    </th>
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <ReceiptText className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Reporte de facturación</h1>
          <p className="text-sm text-muted-foreground">Facturas emitidas a tus centros de costo.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="space-y-3 pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Facturas ({ordenadas.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={exportarXls} disabled={ordenadas.length === 0}>
              <FileSpreadsheet className="mr-1 h-4 w-4" /> Exportar XLS
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2 text-xs text-muted-foreground">
            <label className="flex flex-col">Desde
              <input type="date" className={inputCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta
              <input type="date" className={inputCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">N° Comprobante
              <input className={`${inputCls} w-44`} placeholder="Buscar…" value={comprobante} onChange={(e) => setComprobante(e.target.value)} /></label>
            <label className="flex flex-col">Paciente
              <input className={`${inputCls} w-56`} placeholder="Buscar…" value={paciente} onChange={(e) => setPaciente(e.target.value)} /></label>
            <label className="flex flex-col">Centro de costo
              <select className={`${inputCls} max-w-56`} value={centro} onChange={(e) => setCentro(e.target.value)}>
                <option value="">Todos</option>
                {centrosOpts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            {(desde || hasta || comprobante || paciente || centro) && (
              <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setComprobante(''); setPaciente(''); setCentro(''); }}>Limpiar</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <Th col="fecha">Fecha</Th>
                <Th col="comprobante">N° Comprobante</Th>
                <Th col="tipo">Tipo</Th>
                <Th col="paciente">Paciente</Th>
                <Th col="centro">Centro de costo</Th>
                <Th col="observacion">Observación</Th>
                <Th col="monto" right>Monto</Th>
                <th className="py-2 pr-3">XML</th>
                <th className="py-2 pr-3">PDF</th>
              </tr>
            </thead>
            <tbody>
              {ordenadas.length === 0 && !isFetching && (
                <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin facturas para tus centros de costo.</td></tr>
              )}
              {ordenadas.map((f) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_emision}</td>
                  <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{f.serie_comprobante || '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{f.tipo_comprobante}</td>
                  <td className="py-2 pr-3"><span className="inline-block max-w-56 truncate align-middle">{f.paciente || '—'}</span></td>
                  <td className="py-2 pr-3"><span className="inline-block max-w-48 truncate align-middle" title={f.centro_costo || ''}>{f.centro_costo || '—'}</span></td>
                  <td className="py-2 pr-3"><span className="inline-block max-w-72 truncate align-middle" title={f.observaciones || ''}>{f.observaciones || '—'}</span></td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{money(f.importe_total, f.moneda)}</td>
                  <td className="py-2 pr-3">
                    {urlDoc(f.xml_url)
                      ? <a href={urlDoc(f.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Download className="h-4 w-4" /> XML</a>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-3">
                    {urlDoc(f.ride_url)
                      ? <a href={urlDoc(f.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><FileText className="h-4 w-4" /> PDF</a>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {ordenadas.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="py-2 pr-3" colSpan={6}>Total ({ordenadas.length})</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">{totales.map(([m, v]) => money(v, m)).join(' · ')}</td>
                  <td className="py-2 pr-3" colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
